import {
    Button,
    ContextualContentBox,
    FoodsIcon,
    FoodsTextInput,
    FormBox,
    Typography,
    useFormBaseContext,
    WhiteBox,
} from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IngredientReferenceField } from "./IngredientReferenceField";
import { FilliereField } from "./FilliereField";
import { RadioBooleanField } from "src/components/Radio/RadioBooleanField";
import { IngredientFormType } from "../../IngredientForm.types";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { AgribalyseInfoDrawer } from "src/components/Drawer/AgribalyseInfo";
import { IngredientTagField } from "./Fields/IngredientTagField";
import { IngredientJsonAttributesField } from "./Fields/IngredientJsonAttributesField";
import { OwnedByField } from "./Fields/OwnedByField";
import { AssociatedStructuresField } from "./Fields/AssociatedStructuresField";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { ingredientFormSchema, type IngredientFormData } from "../../IngredientForm.schema";

type Page1Props = {
    ingredient: IngredientFormType;
    setIngredient: Dispatch<SetStateAction<IngredientFormType>>;
};

const Page1 = (props: Page1Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    return (
        <Stack>
            <Stack flexDirection={"row"} alignItems={"center"} gap={theme.spacing(2)} padding={theme.spacing(2, 0)}>
                <FoodsIcon size={8} icon={"information"} />
                <Stack>
                    <Typography variant={"h1"}>{t("common.general_information", "General information")}</Typography>
                    <Typography variant={"body-regular"}>
                        {t(
                            "entity.ingredient_page.subtitle",
                            "Provide the ingredient information. Fields marked with an * are required to proceed.",
                        )}
                    </Typography>
                </Stack>
            </Stack>
            <Stack flexDirection={"row"} gap={theme.spacing(2)}>
                <Stack
                    sx={{
                        flex: 3,
                    }}
                >
                    <BaseInformation {...props} />
                </Stack>
                <FormBox
                    sx={{
                        position: "sticky",
                        top: "120px",
                        flex: 1.5,
                        padding: 0,
                    }}
                >
                    <InformationPanel />
                </FormBox>
            </Stack>
        </Stack>
    );
};
export default Page1;

const BaseInformation = (props: Page1Props) => {
    const { ingredient, setIngredient } = props;
    const { t } = useTranslation();
    const { company } = useCompanyMetadata();
    const { lockForward, unlockForward } = useFormBaseContext();
    const theme = useTheme();

    const {
        control,
        formState: { errors, isValid },
    } = useForm<IngredientFormData>({
        resolver: zodResolver(ingredientFormSchema),
        mode: "onChange",
        reValidateMode: "onBlur",
        defaultValues: {
            name: ingredient.name ?? "",
            clientId: ingredient.clientId ?? "",
            ingredientReferenceId: ingredient.ingredientReferenceId ?? undefined,
            organic: ingredient.organic ?? undefined,
            ownedBy: ingredient.ownedBy ?? company,
            ingredientLine: ingredient.ingredientLine ?? undefined,
            tags: ingredient.tags ?? {},
        },
    });

    const isOwner = ingredient.ownedBy?.id === company.id;

    useEffect(() => {
        if (isValid) {
            unlockForward();
        } else {
            lockForward(t("form.invalidstep"));
        }
    }, [isValid]);

    return (
        <Stack gap={theme.spacing(3)}>
            <WhiteBox>
                <Stack padding={theme.spacing(4)} gap={theme.spacing(4)}>
                    <Stack gap={theme.spacing(0.5)}>
                        <Typography variant={"h2"}>
                            {t("entity.ingredient_identity.section_title", "Ingredient Identity")}
                        </Typography>
                        <Typography variant={"body-regular"} sx={{ color: theme.custom.grey[2000] }}>
                            {t(
                                "entity.ingredient_identity.section_subtitle",
                                "Define the information used to identify your ingredient and calculate its indicators.",
                            )}
                        </Typography>
                    </Stack>

                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <Stack flexDirection={"column"} gap={theme.spacing(1)}>
                                <Typography variant="body-semibold">{`${t("ingredient.identity.name", "Ingredient name")}*`}</Typography>
                                <FoodsTextInput
                                    {...field}
                                    error={!!errors.name}
                                    onChange={(value: string) => {
                                        field.onChange(value);
                                        setIngredient((prevState: IngredientFormType) => ({
                                            ...prevState,
                                            name: value,
                                        }));
                                    }}
                                />
                            </Stack>
                        )}
                    />
                    <Controller
                        name="clientId"
                        control={control}
                        render={({ field }) => (
                            <Stack flexDirection={"column"} gap={theme.spacing(1)}>
                                <Typography variant="body-semibold">
                                    {t("product.identity.unique_identifier", "Unique identifier")}
                                </Typography>
                                <FoodsTextInput
                                    {...field}
                                    value={field.value ?? ""}
                                    error={!!errors.clientId}
                                    onChange={(value: string) => {
                                        field.onChange(value);
                                        setIngredient((prevState: IngredientFormType) => ({
                                            ...prevState,
                                            clientId: value,
                                        }));
                                    }}
                                />
                            </Stack>
                        )}
                    />

                    <Controller
                        name="ingredientReferenceId"
                        control={control}
                        render={({ field }) => (
                            <IngredientReferenceField ingredient={ingredient} onChange={setIngredient} field={field} />
                        )}
                    />

                    <Controller
                        name="organic"
                        control={control}
                        render={({ field }) => (
                            <RadioBooleanField
                                title={t("ingredient.identity.organic", "Organic ingredient")}
                                // required={true}
                                description={""}
                                value={field.value ?? undefined}
                                onChange={(value) => {
                                    field.onChange(value);
                                    setIngredient((prevState: IngredientFormType) => ({
                                        ...prevState,
                                        organic: value,
                                    }));
                                }}
                            />
                        )}
                    />
                </Stack>
            </WhiteBox>

            <WhiteBox>
                <Stack padding={theme.spacing(4)} gap={theme.spacing(4)}>
                    <Stack gap={theme.spacing(0.5)}>
                        <Typography variant={"h2"}>{t("entity.organization.section_title", "Organisation")}</Typography>
                        <Typography variant={"body-regular"} sx={{ color: theme.custom.grey[2000] }}>
                            {t(
                                "entity.organization.section_subtitle",
                                "Define the ingredient’s positioning within your organization and the entities that will be able to contribute to it.",
                            )}
                        </Typography>
                    </Stack>

                    <Controller
                        name="ingredientLine"
                        control={control}
                        render={({ field }) => (
                            <FilliereField ingredient={ingredient} onChange={setIngredient} field={field} />
                        )}
                    />

                    {isOwner === false && (
                        <Controller
                            name="ownedBy"
                            control={control}
                            render={({
                                field,
                                // onChange
                            }) => (
                                <OwnedByField
                                    value={field.value ?? null}
                                    onChange={(v) => {
                                        // No need the onChange as the value is put at init
                                        // I put it here for clarity :)
                                        // onChange(v);

                                        // However needs synchronization, there's a useEffect inside the component
                                        setIngredient((prevState: IngredientFormType) => {
                                            return {
                                                ...prevState,
                                                ownedBy: v,
                                            };
                                        });
                                    }}
                                />
                            )}
                        />
                    )}

                    {isOwner && (
                        <AssociatedStructuresField
                            value={ingredient.shares}
                            onChange={(value) => {
                                setIngredient((prevState: IngredientFormType) => ({
                                    ...prevState,
                                    shares: value,
                                }));
                            }}
                            ownedBy={ingredient.ownedBy}
                        />
                    )}
                </Stack>
            </WhiteBox>

            <WhiteBox>
                <Stack padding={theme.spacing(4)} gap={theme.spacing(4)}>
                    <Stack gap={theme.spacing(0.5)}>
                        <Typography variant={"h2"}>{t("entity.tags.title", "Tags")}</Typography>
                        <Typography variant={"body-regular"} sx={{ color: theme.custom.grey[2000] }}>
                            {t(
                                "entity.ingredient.tags.subtitle",
                                "Add tags to facilitate the classification and analysis of your ingredient.",
                            )}
                        </Typography>
                    </Stack>

                    <Controller
                        name="tags"
                        control={control}
                        render={({ field }) => (
                            <IngredientTagField ingredient={ingredient} setIngredient={setIngredient} field={field} />
                        )}
                    />
                </Stack>
            </WhiteBox>

            {/* JSON Attributes Field - handled separately */}
            <IngredientJsonAttributesField ingredient={ingredient} setIngredient={setIngredient} />

            <Box>
                <Typography variant="body">{t("form.mandatory_field")}</Typography>
            </Box>
        </Stack>
    );
};

const InformationPanel = () => {
    const { t } = useTranslation();

    const [open, setOpen] = useState(false);
    return (
        <Box>
            <ContextualContentBox
                size="small"
                title={t(
                    "ingredient.identity.why_associate_agribalyse",
                    "Why do you need to associate your ingredient with an Agribalyse reference?",
                )}
            >
                <Box>
                    <Typography variant="body">{t("info.agribalyse_ingredient_1")}</Typography>
                    <Button variant="text" onClick={() => setOpen(true)} endIcon={<ArrowForwardIcon />}>
                        {t("common.find_out_more", "Find out more")}
                    </Button>
                </Box>
            </ContextualContentBox>
            <AgribalyseInfoDrawer isOpen={open} setOpen={setOpen} />
        </Box>
    );
};
